import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Flag } from 'lucide-react';
import api from '../api/client';
import './CalendarWidget.css';

interface CalendarEvent {
  uid: string;
  summary: string;
  description: string;
  startUTC: string;
  endUTC: string;
  sourceType: 'championship' | 'server';
  championshipId?: string;
  roundId?: string;
  raceId?: string;
}

interface CalendarWidgetProps {
  onSelectChampionship?: (championshipId: string) => void;
  displayTimezone?: string;
  steamId?: string;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const PREVIEW_COUNT = 5;

function isSameDay(a: Date, b: Date, tz: string): boolean {
  const fmt = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: tz });
  return fmt(a) === fmt(b);
}

function localDay(date: Date, tz: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day') };
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function firstWeekdayOfMonth(year: number, month: number): number {
  // Monday-first; Sunday = 6
  const d = new Date(year, month - 1, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

function displayName(summary: string): string {
  // Strip leading "ABBR - " when the next token is a round ID like S01R01
  return summary.replace(/^.+? - (?=S\d+R\d+)/, '');
}

function formatEventTime(dateStr: string, tz: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: tz });
}

function formatEventDate(dateStr: string, tz: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: tz });
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({
  onSelectChampionship,
  displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  steamId,
}) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [registeredUids, setRegisteredUids] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [calPage, setCalPage] = useState(0); // months offset from current

  const now = new Date();
  const { year: curYear, month: curMonth } = localDay(now, displayTimezone);

  const viewYear = curYear + Math.floor((curMonth - 1 + calPage) / 12);
  const viewMonth = ((curMonth - 1 + calPage) % 12 + 12) % 12 + 1;

  useEffect(() => {
    setLoading(true);
    api.getUpcomingCalendarEvents()
      .then((res) => setEvents(res.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!steamId) { setRegisteredUids(new Set()); return; }
    api.getRegisteredCalendarEvents(steamId)
      .then((res) => {
        const uids = new Set<string>((res.data.events || []).map((e: CalendarEvent) => e.uid));
        setRegisteredUids(uids);
      })
      .catch(() => setRegisteredUids(new Set()));
  }, [steamId]);

  // Events that fall within the viewed month
  const eventsInMonth = useMemo(() => {
    return events.filter((ev) => {
      const { year, month } = localDay(new Date(ev.startUTC), displayTimezone);
      return year === viewYear && month === viewMonth;
    });
  }, [events, viewYear, viewMonth, displayTimezone]);

  // Map day-of-month → events on that day
  const eventsByDay = useMemo(() => {
    const map = new Map<number, CalendarEvent[]>();
    for (const ev of eventsInMonth) {
      const { day } = localDay(new Date(ev.startUTC), displayTimezone);
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(ev);
    }
    return map;
  }, [eventsInMonth, displayTimezone]);

  const previewEvents = useMemo(() => events.slice(0, PREVIEW_COUNT), [events]);

  const totalDays = daysInMonth(viewYear, viewMonth);
  const firstWeekday = firstWeekdayOfMonth(viewYear, viewMonth);
  const todayLocal = localDay(now, displayTimezone);

  return (
    <div className={`cal-widget${expanded ? ' cal-expanded' : ''}`}>
      <div className="cal-widget-header">
        <span className="cal-widget-title"><Calendar size={15} /> Race Calendar</span>
        <button
          type="button"
          className="cal-expand-btn"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Expand'}
        </button>
      </div>

      {!expanded && (
        <div className="cal-preview-list">
          {loading && <div className="cal-empty">Loading…</div>}
          {!loading && previewEvents.length === 0 && (
            <div className="cal-empty">No upcoming races scheduled.</div>
          )}
          {!loading && previewEvents.map((ev) => {
            const isRegistered = registeredUids.has(ev.uid);
            const isToday = isSameDay(new Date(ev.startUTC), now, displayTimezone);
            return (
              <button
                key={ev.uid}
                type="button"
                className={`cal-event-row${isRegistered ? ' cal-event-registered' : ''}${ev.championshipId && onSelectChampionship ? ' cal-event-clickable' : ''}`}
                onClick={() => ev.championshipId && onSelectChampionship && onSelectChampionship(ev.championshipId)}
                disabled={!ev.championshipId || !onSelectChampionship}
              >
                <div className="cal-event-date-col">
                  <span className={`cal-event-date${isToday ? ' cal-event-today' : ''}`}>
                    {formatEventDate(ev.startUTC, displayTimezone)}
                  </span>
                  <span className="cal-event-time">{formatEventTime(ev.startUTC, displayTimezone)}</span>
                </div>
                <div className="cal-event-info-col">
                  <span className="cal-event-name" title={ev.summary}>{displayName(ev.summary)}</span>
                  {isRegistered && <span className="cal-registered-pill"><Flag size={10} /> Registered</span>}
                </div>
              </button>
            );
          })}
          {!loading && events.length > PREVIEW_COUNT && (
            <button type="button" className="cal-show-more" onClick={() => setExpanded(true)}>
              +{events.length - PREVIEW_COUNT} more races
            </button>
          )}
        </div>
      )}

      {expanded && (
        <div className="cal-full">
          {/* Month navigation */}
          <div className="cal-month-nav">
            <button
              type="button"
              className="cal-nav-btn"
              onClick={() => setCalPage((p) => p - 1)}
              disabled={calPage <= 0}
            >
              <ChevronLeft size={15} />
            </button>
            <span className="cal-month-label">{MONTH_NAMES[viewMonth - 1]} {viewYear}</span>
            <button
              type="button"
              className="cal-nav-btn"
              onClick={() => setCalPage((p) => p + 1)}
            >
              <ChevronRight size={15} />
            </button>
          </div>

          {/* Grid */}
          <div className="cal-grid">
            {DAY_LABELS.map((d) => (
              <div key={d} className="cal-grid-header">{d}</div>
            ))}
            {/* Leading empty cells */}
            {Array.from({ length: firstWeekday }, (_, i) => (
              <div key={`empty-${i}`} className="cal-grid-cell cal-grid-cell-empty" />
            ))}
            {/* Day cells */}
            {Array.from({ length: totalDays }, (_, i) => {
              const day = i + 1;
              const cellEvents = eventsByDay.get(day) || [];
              const isToday = todayLocal.year === viewYear && todayLocal.month === viewMonth && todayLocal.day === day;
              const hasRegistered = cellEvents.some((ev) => registeredUids.has(ev.uid));
              return (
                <div
                  key={day}
                  className={`cal-grid-cell${isToday ? ' cal-grid-today' : ''}${cellEvents.length > 0 ? ' cal-grid-has-event' : ''}`}
                >
                  <span className="cal-grid-day-num">{day}</span>
                  {cellEvents.length > 0 && (
                    <div className="cal-grid-dots">
                      {cellEvents.slice(0, 3).map((ev) => (
                        <button
                          key={ev.uid}
                          type="button"
                          className={`cal-grid-dot${registeredUids.has(ev.uid) ? ' cal-grid-dot-registered' : ''}`}
                          title={ev.summary}
                          onClick={() => ev.championshipId && onSelectChampionship && onSelectChampionship(ev.championshipId)}
                        />
                      ))}
                      {cellEvents.length > 3 && <span className="cal-grid-dot-overflow">+{cellEvents.length - 3}</span>}
                    </div>
                  )}
                  {hasRegistered && <span className="cal-grid-reg-indicator" title="You are registered for an event on this day" />}
                </div>
              );
            })}
          </div>

          {/* Event list for this month */}
          <div className="cal-month-events">
            {eventsInMonth.length === 0 && (
              <div className="cal-empty">No events this month.</div>
            )}
            {eventsInMonth.map((ev) => {
              const isRegistered = registeredUids.has(ev.uid);
              return (
                <button
                  key={ev.uid}
                  type="button"
                  className={`cal-event-row${isRegistered ? ' cal-event-registered' : ''}${ev.championshipId && onSelectChampionship ? ' cal-event-clickable' : ''}`}
                  onClick={() => ev.championshipId && onSelectChampionship && onSelectChampionship(ev.championshipId)}
                  disabled={!ev.championshipId || !onSelectChampionship}
                >
                  <div className="cal-event-date-col">
                    <span className="cal-event-date">{formatEventDate(ev.startUTC, displayTimezone)}</span>
                    <span className="cal-event-time">{formatEventTime(ev.startUTC, displayTimezone)}</span>
                  </div>
                  <div className="cal-event-info-col">
                    <span className="cal-event-name" title={ev.summary}>{displayName(ev.summary)}</span>
                    {isRegistered && <span className="cal-registered-pill"><Flag size={10} /> Registered</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarWidget;
